import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { QrCode } from 'lucide-react';
import { contactsAPI } from '../services/api';
import QRCodeModal from './QRCodeModal';
import './RegistrationTypeSelection.css';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:8000/api/v1';

const RegistrationTypeSelection = () => {
    const { eventNameId } = useParams();
    const navigate = useNavigate();
    const [event, setEvent] = useState(null);
    const [eventEntities, setEventEntities] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [selectedEntityId, setSelectedEntityId] = useState('');
    const [entityError, setEntityError] = useState('');
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [eventRegistrationGuid, setEventRegistrationGuid] = useState('');
    const [qrModalOpen, setQrModalOpen] = useState(false);
    const [qrCodeLink, setQrCodeLink] = useState(null);
    const [loadingQRCode, setLoadingQRCode] = useState(false);

    useEffect(() => {
        // Get EventRegistrationGuid from localStorage
        const storedGuid = localStorage.getItem('eventRegistrationGuid');
        if (!storedGuid) {
            // If no GUID, redirect back to email registration
            navigate(`/event/${eventNameId}/registration`);
            return;
        }
        setEventRegistrationGuid(storedGuid);

        const fetchEventData = async () => {
            try {
                // Fetch both event details and event entities
                const [eventResponse, entitiesResponse] = await Promise.all([
                    axios.get(`${API_BASE_URL}/public/events/${eventNameId}`),
                    axios.get(`${API_BASE_URL}/public/event-entities/${eventNameId}`)
                ]);

                setEvent(eventResponse.data);
                setEventEntities(entitiesResponse.data);
            } catch (err) {
                setError('Failed to load event data');
            } finally {
                setLoading(false);
            }
        };

        if (eventNameId) {
            fetchEventData();
        }
    }, [eventNameId, navigate]);

    const handleEntityChange = (e) => {
        const value = e.target.value;
        setSelectedEntityId(value);
        if (value) {
            setEntityError('');
        }
    };

    const handleNext = async () => {
        if (!selectedEntityId) {
            setEntityError('Please select a registration type');
            return;
        }

        setIsSubmitting(true);
        try {
            await axios.put(`${API_BASE_URL}/public/event-registrations/${eventRegistrationGuid}`, {
                event_entity_id: parseInt(selectedEntityId)
            });

            // Navigate to the registration form
            navigate(`/event/${eventNameId}/registration/form`);
        } catch (err) {
            setError('Failed to update registration. Please try again.');
        } finally {
            setIsSubmitting(false);
        }
    };


    const formatDate = (dateString) => {
        const date = new Date(dateString);
        return date.toLocaleDateString('en-US', {
            year: 'numeric',
            month: 'long',
            day: 'numeric'
        });
    };

    const formatTime = (dateString) => {
        const date = new Date(dateString);
        return date.toLocaleTimeString('en-US', {
            hour: 'numeric',
            minute: '2-digit',
            hour12: true
        });
    };

    const fetchQRCodeLink = async () => {
        if (qrCodeLink) {
            setQrModalOpen(true);
            return;
        }
        
        setLoadingQRCode(true);
        try {
            const response = await contactsAPI.getMyQRCodeLink();
            if (response.data?.success && response.data?.qr_code_link) {
                setQrCodeLink(response.data.qr_code_link);
                setQrModalOpen(true);
            } else {
                // Fallback to current URL if QR code link not found
                setQrCodeLink(window.location.href);
                setQrModalOpen(true);
            }
        } catch (error) {
            console.error('Error fetching QR code link:', error);
            // Fallback to current URL on error
            setQrCodeLink(window.location.href);
            setQrModalOpen(true);
        } finally {
            setLoadingQRCode(false);
        }
    };

    if (loading) {
        return <div className="loading">Loading registration options...</div>;
    }

    if (error || !event) {
        return <div className="error">{error || 'Event not found'}</div>;
    }

    return (
        <div className="registration-type-selection">
            {/* QR Code Modal */}
            <QRCodeModal
                isOpen={qrModalOpen}
                onClose={() => setQrModalOpen(false)}
                url={qrCodeLink || window.location.href}
                title="Event Ticket"
            />

            {/* QR Code Button - Top Left */}
            <button
                className="qr-code-button"
                onClick={fetchQRCodeLink}
                disabled={loadingQRCode}
                aria-label="Show QR Code"
                title="Show QR Code for mobile access"
            >
                <QrCode size={20} />
            </button>

            {/* Event Banner with Logo */}
            <div className="event-banner">
                {event.EventImage && (
                    <img src={event.EventImage} alt="Event Logo" className="event-logo" />
                )}
            </div>

            {/* Event Title */}
            <div className="event-header">
                <h1 className="event-title">{event.Name}</h1>
            </div>

            {/* Event Details */}
            <div className="event-details">
                <div className="detail-item location-item">
                    <span className="detail-text">
                        <i className="fas fa-map-marker-alt detail-icon"></i>
                        {event.Location}
                        {event.Address1 && ` - ${event.Address1}`}
                        {event.City && `, ${event.City}`}
                        {event.StateProvince && `, ${event.StateProvince}`}
                        {event.Country && `, ${event.Country}`}
                    </span>
                </div>

                <div className="detail-item date-time-row">
                    <div className="date-section">
                        <i className="fas fa-calendar-alt detail-icon"></i>
                        <span className="detail-text">
                            {formatDate(event.StartDate)} - {formatDate(event.EndDate)}
                        </span>

                        <i className="fas fa-clock detail-icon"></i>
                        <span className="detail-text">
                            {formatTime(event.StartDate)} - {formatTime(event.EndDate)} {event.TimeZone || 'EDT'}
                        </span>
                    </div>

                </div>
            </div>

            {/* Separator */}
            <div className="separator"></div>

            {/* Registration Section */}
            <div className="registration-section">
                <h2 className="registration-prompt">Please select your registration type to begin</h2>

                <div className="form-group">
                    <div className="select-wrapper">
                        <select
                            id="registrationType"
                            value={selectedEntityId}
                            onChange={handleEntityChange}
                            className={`form-select ${entityError ? 'error' : ''}`}
                        >
                            <option value="">Select an option</option>
                            {eventEntities.map((entity) => (
                                <option key={entity.EventEntityId} value={entity.EventEntityId}>
                                    {entity.Name}
                                </option>
                            ))}
                        </select>
                        <i className="fas fa-chevron-down select-icon"></i>
                    </div>
                    {entityError && <span className="error-message">{entityError}</span>}
                </div>
            </div>

            {/* Navigation Buttons */}
            <div className="navigation-buttons">
                <button
                    type="button"
                    className={`next-button ${selectedEntityId ? 'active' : ''}`}
                    onClick={handleNext}
                    disabled={!selectedEntityId || isSubmitting}
                >
                    {isSubmitting ? 'Submitting...' : 'Next'}
                </button>
            </div>
        </div>
    );
};

export default RegistrationTypeSelection;
